import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, resolveApiMediaUrl } from '../api/client.js';
import { useTheme } from '../theme/ThemeContext.jsx';
import { parseStoreAccent, storeAccentCssVariables } from '../theme/storeAccent.js';
import { StorePwaBootstrap } from '../components/store/StorePwaBootstrap.jsx';

function SunIcon(props) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      {...props}
    >
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
    </svg>
  );
}

function MoonIcon(props) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      {...props}
    >
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}

/** Página informativa quando o visitante indica ser menor de idade (vitrine com gate 18+). */
export function StoreAgeRestrictionPage() {
  const { slug } = useParams();
  const { scheme, toggle } = useTheme();
  const [tenant, setTenant] = useState(null);
  const [err, setErr] = useState('');
  const [logoBroken, setLogoBroken] = useState(false);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    api(`/store/${encodeURIComponent(slug)}/catalog`, { skipAuth: true })
      .then((payload) => {
        if (cancelled) return;
        const t = payload?.tenant;
        setTenant(t && typeof t === 'object' ? t : null);
        setErr('');
      })
      .catch((e) => {
        if (cancelled) return;
        setErr(e.message || 'Não foi possível carregar os dados da loja.');
        setTenant(null);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const accentStyle = useMemo(
    () => storeAccentCssVariables(parseStoreAccent(tenant?.theme_json)),
    [tenant?.theme_json],
  );

  const lojaPath = slug ? `/${encodeURIComponent(slug)}` : '/';
  const logoSrc =
    tenant?.logo_url && !logoBroken ? resolveApiMediaUrl(String(tenant.logo_url)) : '';
  const nome = tenant?.nome ? String(tenant.nome) : slug || 'Loja';

  return (
    <div
      className="relative min-h-screen overflow-hidden bg-zinc-100 text-zinc-900 dark:bg-zinc-950 dark:text-zinc-100"
      style={accentStyle}
    >
      <StorePwaBootstrap slug={slug ? String(slug).toLowerCase() : ''} tenant={tenant} />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.55] dark:opacity-40"
        style={{
          backgroundImage: `
            radial-gradient(ellipse 120% 80% at 10% -10%, rgb(var(--st-rgb) / 0.22), transparent 50%),
            radial-gradient(ellipse 90% 70% at 100% 20%, rgba(225, 29, 72, 0.12), transparent 55%),
            radial-gradient(ellipse 80% 60% at 50% 110%, rgba(39, 39, 42, 0.08), transparent 45%)
          `,
        }}
      />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.035] dark:opacity-[0.06]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160' viewBox='0 0 160 160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='160' height='160' filter='url(%23n)' opacity='0.55'/%3E%3C/svg%3E")`,
        }}
      />

      <header className="relative z-10 border-b border-zinc-200/60 bg-white/70 backdrop-blur-xl dark:border-zinc-800/60 dark:bg-zinc-900/65">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-3 px-4 py-4 sm:px-6">
          <Link
            to="/"
            className="font-display text-sm font-semibold text-zinc-600 transition hover:text-[rgb(var(--st-rgb))] dark:text-zinc-400 dark:hover:text-[var(--st-400)]"
          >
            ← Início OminIA
          </Link>
          <button
            type="button"
            onClick={toggle}
            aria-label={scheme === 'dark' ? 'Ativar modo claro' : 'Ativar modo escuro'}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-zinc-200 text-zinc-600 transition hover:border-[rgb(var(--st-rgb)/0.4)] hover:bg-[rgb(var(--st-rgb)/0.08)] dark:border-zinc-600 dark:text-zinc-300"
          >
            {scheme === 'dark' ? <SunIcon className="h-5 w-5" /> : <MoonIcon className="h-5 w-5" />}
          </button>
        </div>
      </header>

      <main className="relative z-10 mx-auto flex min-h-[calc(100vh-5rem)] max-w-3xl flex-col items-center justify-center px-4 py-12 sm:px-6">
        <div className="w-full max-w-lg rounded-[1.85rem] border border-zinc-200/90 bg-white/90 p-8 shadow-[0_32px_100px_-40px_rgba(0,0,0,0.45)] backdrop-blur-sm dark:border-zinc-700/70 dark:bg-zinc-900/85 sm:p-10">
          <div className="flex flex-col items-center text-center">
            {logoSrc ? (
              <img
                src={logoSrc}
                alt=""
                className="mb-6 h-16 w-auto max-w-[220px] object-contain sm:h-[4.5rem]"
                loading="eager"
                onError={() => setLogoBroken(true)}
              />
            ) : (
              <div
                className="mb-6 flex h-16 w-16 items-center justify-center rounded-2xl border border-zinc-200 bg-zinc-50 text-2xl font-display font-bold text-zinc-400 dark:border-zinc-600 dark:bg-zinc-800/80 dark:text-zinc-500"
                aria-hidden
              >
                18+
              </div>
            )}
            <p className="text-[10px] font-bold uppercase tracking-[0.32em] text-rose-600/90 dark:text-rose-400/95">
              Conteúdo restrito
            </p>
            <h1 className="mt-3 font-display text-2xl font-bold leading-tight text-zinc-900 dark:text-zinc-50 sm:text-[1.75rem]">
              {nome}
            </h1>
            <p className="mt-5 text-sm leading-relaxed text-zinc-600 dark:text-zinc-400">
              Obrigado por visitar. Esta loja indicou que o catálogo ou as mensagens podem incluir conteúdo inadequado
              para menores de idade. Por isso, o acesso à vitrine não está disponível para quem ainda não completou 18
              anos.
            </p>
            <p className="mt-4 text-sm leading-relaxed text-zinc-600 dark:text-zinc-400">
              Se chegou aqui por engano e já é maior de idade, pode voltar e confirmar a sua idade na entrada da loja.
            </p>
            {err ? (
              <p className="mt-5 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-xs text-amber-950 dark:text-amber-100">
                {err}
              </p>
            ) : null}
            <div className="mt-8 flex w-full flex-col gap-3 sm:flex-row sm:justify-center">
              <Link
                to={lojaPath}
                className="inline-flex flex-1 items-center justify-center rounded-2xl bg-[rgb(var(--st-rgb))] px-5 py-3.5 text-center text-sm font-bold text-white shadow-[0_14px_40px_-14px_rgb(var(--st-rgb)/0.8)] transition hover:brightness-105 sm:flex-initial sm:min-w-[11rem]"
              >
                Sou maior de idade
              </Link>
              <Link
                to="/"
                className="inline-flex flex-1 items-center justify-center rounded-2xl border border-zinc-200 bg-white px-5 py-3.5 text-center text-sm font-semibold text-zinc-800 transition hover:bg-zinc-50 dark:border-zinc-600 dark:bg-zinc-800/70 dark:text-zinc-100 dark:hover:bg-zinc-800 sm:flex-initial sm:min-w-[11rem]"
              >
                Sair
              </Link>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
